import React, { createContext, useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { firebaseTaskService } from '../services/firebaseTaskService';

export const TaskContext = createContext(null);

export const TaskProvider = ({ children }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { currentUser } = useAuth();

  // Load tasks when user is available
  useEffect(() => {
    if (!currentUser) {
      setTasks([]);
      setLoading(false);
      return;
    }

    // Load tasks once
    setLoading(true);
    firebaseTaskService.getUserTasks(currentUser.uid)
      .then(tasks => {
        setTasks(tasks);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error loading tasks:', error);
        setError('Failed to load tasks');
        setLoading(false);
      });
  }, [currentUser]);

  // Add task
  const addTask = async (taskData) => {
    if (!currentUser) throw new Error('User not authenticated');
    
    const newTask = await firebaseTaskService.createTask(currentUser.uid, taskData);
    setTasks(prev => [newTask, ...prev]);
    return newTask;
  };

  // Update task
  const updateTask = async (id, updates) => {
    if (!currentUser) throw new Error('User not authenticated');
    
    await firebaseTaskService.updateTask(id, updates, currentUser.uid);
    setTasks(prev => prev.map(task => 
      task.id === id ? { ...task, ...updates } : task
    ));
  };

  // Delete task
  const deleteTask = async (id) => {
    await firebaseTaskService.deleteTask(id);
    setTasks(prev => prev.filter(task => task.id !== id));
  };

  return (
    <TaskContext.Provider value={{
      tasks,
      loading,
      error,
      addTask,
      updateTask,
      deleteTask,
      setTasks,
      setError
    }}>
      {children}
    </TaskContext.Provider>
  );
};

export const useTasks = () => {
  const context = React.useContext(TaskContext);
  if (!context) {
    throw new Error('useTasks must be used within a TaskProvider');
  }
  return context;
};